import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import {
  useMarkAllRead,
  useMarkNotificationRead,
  useNotifications,
  type AppNotification,
} from './notifications-api';

const RELATIVE = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });

function fromNow(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (Math.abs(mins) < 60) return RELATIVE.format(-mins, 'minute');
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return RELATIVE.format(-hours, 'hour');
  return RELATIVE.format(-Math.round(hours / 24), 'day');
}

export function NotificationBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllRead();

  function handleClick(n: AppNotification) {
    setAnchor(null);
    if (!n.readAt) markRead.mutate(n.id);
    if (n.metadata?.ticketId) {
      navigate(`/app/tickets/${n.metadata.ticketId}`);
    }
  }

  const unread = data?.unread ?? 0;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={`Notifications${unread ? `, ${unread} non lue${unread > 1 ? 's' : ''}` : ''}`}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          sx={{ color: 'inherit' }}
        >
          <Badge badgeContent={unread} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ list: { 'aria-label': 'Notifications', sx: { width: 380, p: 0 } } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">Notifications</Typography>
          {unread > 0 && (
            <Button size="small" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
              Tout marquer lu
            </Button>
          )}
        </Stack>
        <Divider />
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} aria-label="Chargement" />
          </Box>
        )}
        {data && data.data.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">Pas de notification.</Typography>
          </Box>
        )}
        {data && data.data.length > 0 && (
          <List dense sx={{ maxHeight: 480, overflow: 'auto', p: 0 }}>
            {data.data.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => handleClick(n)}
                sx={{
                  borderLeft: 3,
                  borderColor: n.readAt ? 'transparent' : 'primary.main',
                  bgcolor: n.readAt ? 'transparent' : 'action.hover',
                }}
              >
                <ListItemText
                  primary={n.title}
                  secondary={
                    <>
                      {n.body && <Typography component="span" variant="caption" display="block">{n.body}</Typography>}
                      <Typography component="span" variant="caption" color="text.secondary">{fromNow(n.createdAt)}</Typography>
                    </>
                  }
                  primaryTypographyProps={{ variant: 'body2', fontWeight: n.readAt ? 400 : 600 }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
}
